import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHistory, useParams } from 'react-router';

import annotatorConstants from '@/modules/annotator/annotator.constants';
import type { Appointment } from '@/react-query/useAppointments';
import type { ValidUniversalToothId } from '@videa/utils';

import { selectedVisitAtom } from '../../../../app/appHeader/imageViewerMenu/atoms';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { imageFilterAtom } from '../../../annotator/atoms';
import {
  enabledImageTypesAtom,
  imageBrowserExpandedAtom,
  imageBrowserOpenAtom,
} from '../../../annotator/multiFileViewer/findingViewer/imageBrowser/atoms';
import type { QuadrantNumber } from '../../atoms';
import { selectQuadrantsAtom, selectTeethAtom } from '../../atoms';

import type { ToothStatus } from './Tooth';
import type { TDisplayOrderQuadrantAbbreviations } from './types';

export const quadrantToNumber = {
  UR: 1,
  UL: 2,
  LL: 3,
  LR: 4,
} as const;

export const useToothSelection = (appointment?: Appointment) => {
  const { id: selectedPatientId, imageId } = useParams<{
    id: string;
    imageId?: string;
  }>();
  const history = useHistory();

  const currentImageFilter = useAtomValue(imageFilterAtom);
  const [selectedTeeth, setSelectedTeeth] = useAtom(selectTeethAtom);
  const [enabledImageTypes, setEnabledImageTypes] = useAtom(
    enabledImageTypesAtom,
  );

  const setCurrentImageFilter = useSetAtom(imageFilterAtom);
  const setImageBrowserExpanded = useSetAtom(imageBrowserExpandedAtom);
  const setImageBrowserOpen = useSetAtom(imageBrowserOpenAtom);

  const visitId = useAtomValue(selectedVisitAtom);

  const { trackEvent } = useTrackEvent();
  const [selectedQuadrants, setSelectedQuadrants] =
    useAtom(selectQuadrantsAtom);

  const trackToothSelection = (
    teethSelected: string[],
    quadrantsSelected?: QuadrantNumber[],
    module: 'patient-list' | 'image-viewer' = 'image-viewer',
  ) => {
    trackEvent(mixpanelEvents.miniOdontogramToothSelection, {
      teeth: teethSelected,
      select: 'multiselect',
      dentition: 'adult',
      imageFilter: [currentImageFilter],
      estimatedVisitId: visitId,
      patientId: selectedPatientId,
      selectedQuadrants: quadrantsSelected ?? selectedQuadrants,
      module,
      unifiedId: appointment?.appointment.unifiedId,
      pmsAppointmentId: appointment?.appointment.pmsAppointmentId,
      pmsPatientId: appointment?.patient.pmsPatientId,
      imagingPatientId: appointment?.patient.imagingPatientId,
    });
  };

  const handleQuadrantSelection = (
    currentQuadrantNumber: TDisplayOrderQuadrantAbbreviations,
    toothStatuses: ToothStatus[],
  ) => {
    const currentQuadrantToothNumbers = toothStatuses
      .filter((tooth) => tooth?.toothId)
      .map((tooth) => tooth?.toothId);
    const isAnyQuadrantSelected = !!selectedQuadrants.length;
    let newSelectedTeeth: string[] = [];
    let newSelectedQuadrants: QuadrantNumber[] = [];

    if (!isAnyQuadrantSelected) {
      // If no quadrant is selected, deselect all currently selected teeth and
      // select the current quadrant instead.
      newSelectedTeeth = currentQuadrantToothNumbers;
      newSelectedQuadrants = [quadrantToNumber[currentQuadrantNumber]];

      setImageBrowserExpanded(true);
      setImageBrowserOpen(true);
    } else {
      const isCurrentQuadrantAlreadySelected = selectedQuadrants.includes(
        quadrantToNumber[currentQuadrantNumber],
      );
      if (isCurrentQuadrantAlreadySelected) {
        // If the user clicked on an already selected quadrant, deselect all
        // teeth in that quadrant.
        const remainingSelectedTeeth = selectedTeeth.filter(
          (toothNumber) =>
            !currentQuadrantToothNumbers.includes(
              toothNumber as ValidUniversalToothId,
            ),
        );
        if (!remainingSelectedTeeth.length) {
          setImageBrowserExpanded(false);
          setImageBrowserOpen(false);
        }
        newSelectedTeeth = remainingSelectedTeeth;
        newSelectedQuadrants = selectedQuadrants.filter(
          (quadrantNumber) =>
            quadrantNumber !== quadrantToNumber[currentQuadrantNumber],
        );
      } else {
        // If some quadrant is already selected but the user clicked on one
        // that is not, then add the teeth in the current quadrant to the
        // selection.
        newSelectedTeeth = selectedTeeth.concat(currentQuadrantToothNumbers);
        newSelectedQuadrants = selectedQuadrants.concat(
          quadrantToNumber[currentQuadrantNumber],
        );
      }
    }

    setSelectedTeeth(newSelectedTeeth);
    setSelectedQuadrants(newSelectedQuadrants);

    if (!newSelectedQuadrants.length && !newSelectedTeeth.length) {
      setCurrentImageFilter(annotatorConstants.FMX);
    } else {
      setCurrentImageFilter(annotatorConstants.ODONTOGRAM);
    }

    trackToothSelection(newSelectedTeeth, newSelectedQuadrants);
  };

  const handleVisitToothSelection = (
    toothId: string,
    module?: 'patient-list' | 'image-viewer',
  ) => {
    setEnabledImageTypes(
      enabledImageTypes.includes('dentalPhotos')
        ? enabledImageTypes
        : [...enabledImageTypes, 'dentalPhotos'],
    );

    const isCurrentToothAlreadySelected = !!selectedTeeth.find(
      (tooth) => tooth === toothId,
    );

    let newSelectedTeeth: string[] = [];

    if (isCurrentToothAlreadySelected) {
      newSelectedTeeth = [];
      setImageBrowserOpen(false);
      setImageBrowserExpanded(false);
      setCurrentImageFilter(annotatorConstants.FMX);
    } else {
      newSelectedTeeth = [toothId];
      setImageBrowserExpanded(true);
      setImageBrowserOpen(true);
      setCurrentImageFilter(annotatorConstants.ODONTOGRAM);
    }

    setSelectedTeeth(newSelectedTeeth);

    // Redirect to patient-level view if on image-specific view
    if (imageId) {
      history.push({
        pathname: `/annotator/${selectedPatientId}`,
        search: history.location.search,
      });
    }

    trackToothSelection(newSelectedTeeth, [], module);
  };

  const handleToothSelection = (
    toothId: string,
    module?: 'patient-list' | 'image-viewer',
  ) => {
    setEnabledImageTypes(
      enabledImageTypes.includes('dentalPhotos')
        ? enabledImageTypes
        : [...enabledImageTypes, 'dentalPhotos'],
    );

    const isCurrentToothAlreadySelected = !!selectedTeeth.find(
      (tooth) => tooth === toothId,
    );

    let newSelectedTeeth: string[] = [];
    let newSelectedQuadrants: QuadrantNumber[] = [];

    if (isCurrentToothAlreadySelected) {
      if (selectedTeeth.length <= 1) {
        setImageBrowserOpen(false);
        setImageBrowserExpanded(false);
      }
      if (selectedQuadrants.length) {
        // If the current tooth is already selected because its quadrant
        // is selected, then unselect everything except the current tooth.
        newSelectedTeeth = [toothId];
        newSelectedQuadrants = [];
      } else {
        // Unselect the current tooth
        newSelectedTeeth = selectedTeeth.filter((tooth) => tooth !== toothId);
      }
    } else {
      if (!selectedTeeth.length) {
        setImageBrowserExpanded(true);
      }
      setImageBrowserOpen(true);

      if (selectedQuadrants.length) {
        // If the current tooth is not selected and a quadrant in which the
        // current tooth is NOT located is selected, then unselect
        // everything except the current tooth.
        newSelectedTeeth = [toothId];
        newSelectedQuadrants = [];
      } else {
        newSelectedTeeth = [...selectedTeeth, toothId];
      }
    }

    if (!newSelectedTeeth.length && !newSelectedQuadrants.length) {
      setCurrentImageFilter(annotatorConstants.FMX);
    } else {
      setCurrentImageFilter(annotatorConstants.ODONTOGRAM);
    }

    setSelectedTeeth(newSelectedTeeth);
    setSelectedQuadrants(newSelectedQuadrants);

    trackToothSelection(newSelectedTeeth, newSelectedQuadrants, module);
  };

  return {
    handleQuadrantSelection,
    handleToothSelection: handleVisitToothSelection,
  };
};
